// The organisation's kind (a centre, or one individual practitioner) and its
// lifecycle status (active / suspended / closed), read once per screen.
//
// A failed read falls back to "organisation, active": the server still refuses
// a licence for a suspended org, so the fallback can only ever OMIT a banner,
// never let something through.

import { getPortalData } from "../../data";
import type { OrgProfile } from "../../data/types";
import { useComfort } from "../ComfortContext";
import { getDictionary } from "../../i18n/dictionary";
import { useOrg } from "./OrgContext";
import { Notice, useDemoVariant, useLoad } from "./ui";

const FALLBACK: OrgProfile = { kind: "organisation", status: "active" };

export function useOrgProfile(): OrgProfile & { loading: boolean } {
  const { orgId } = useOrg();
  const variant = useDemoVariant();
  const { data, loading } = useLoad(
    () => getPortalData().orgProfile(orgId).catch(() => FALLBACK),
    [orgId, variant],
  );
  return { ...(data ?? FALLBACK), loading };
}

/** A calm notice across admin screens when Zooylo has paused or closed the org. */
export function OrgStatusBanner({ status }: { status: OrgProfile["status"] }) {
  const { locale } = useComfort();
  const t = getDictionary(locale).admin.status;
  if (status === "active") return null;
  return (
    <div style={{ marginBottom: 20 }}>
      <Notice tone="attention" title={status === "closed" ? t.closedTitle : t.suspendedTitle}>
        {status === "closed" ? t.closedBody : t.suspendedBody}
      </Notice>
    </div>
  );
}
